import { useCallback, useMemo, useState } from "react";
import type { ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";

import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { previewProjectCheckoutDeletionTargets } from "~/projectCheckoutDeletionClient";
import type {
  ProjectGroupRemovalResult,
  ProjectGroupRemovalTarget,
} from "~/projectGroupRemovalRunner";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import type { RemovableForgeProject } from "./sidebarProjectRemoval";

export function resolveSidebarCloudProjectForTarget(
  project: SidebarProjectSnapshot,
  target: ProjectGroupRemovalTarget,
): RemovableForgeProject | undefined {
  return project.preConnectCloud &&
    target.environmentId === project.environmentId &&
    target.projectId === project.id
    ? project
    : undefined;
}

function buildTargets(
  project: SidebarProjectSnapshot,
  threads: readonly { readonly environmentId: string; readonly projectId: string }[],
): readonly ProjectGroupRemovalTarget[] {
  const members =
    project.memberProjectRefs.length > 0
      ? project.memberProjectRefs
      : [{ environmentId: project.environmentId, projectId: project.id }];
  return members.map((member) => {
    const checkout = project.worktreeMembers.find(
      (candidate) =>
        candidate.environmentId === member.environmentId &&
        candidate.projectId === member.projectId,
    );
    return {
      environmentId: member.environmentId,
      projectId: member.projectId,
      label: checkout?.label ?? project.name,
      path: checkout?.cwd ?? project.preConnectCloud?.workspaceRoot ?? project.cwd,
      threadCount: threads.filter(
        (thread) =>
          thread.environmentId === member.environmentId && thread.projectId === member.projectId,
      ).length,
      kind:
        project.preConnectCloud || classifyEnvironmentId(member.environmentId).isCloud
          ? "cloud"
          : "local",
    };
  });
}

export function useSidebarProjectFileRemoval(
  project: SidebarProjectSnapshot,
  threads: readonly { readonly environmentId: string; readonly projectId: string }[],
) {
  const [results, setResults] = useState<readonly ProjectGroupRemovalResult[]>([]);
  const [previews, setPreviews] = useState<ReadonlyMap<string, ProjectCheckoutDeletionPreview>>(
    new Map(),
  );
  const [isPreviewing, setIsPreviewing] = useState(false);
  const targets = useMemo(() => buildTargets(project, threads), [project, threads]);
  const onDeleteFilesChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setPreviews(new Map());
        setIsPreviewing(false);
        return;
      }
      setIsPreviewing(true);
      void previewProjectCheckoutDeletionTargets(targets, (target) =>
        resolveSidebarCloudProjectForTarget(project, target),
      )
        .then(setPreviews)
        .finally(() => setIsPreviewing(false));
    },
    [project, targets],
  );
  const reset = useCallback(() => {
    setResults([]);
    setPreviews(new Map());
    setIsPreviewing(false);
  }, []);
  return {
    targets,
    results,
    setResults,
    previews,
    isPreviewing,
    onDeleteFilesChange,
    reset,
  };
}
